import { useState } from 'react';
import { cn } from '@/utils/styles';
import { ChevronDown } from 'lucide-react';
import { LOGO_WHITE } from '@/constants/images';
import { SealBarrierIcon } from '@/components/icons/SealBarrierIcon';
import { FuelFoundationIcon } from '@/components/icons/FuelFoundationIcon';
import { ReadyEnvironmentIcon } from '@/components/icons/ReadyEnvironmentIcon';
import { useATSData, getATSSections } from './useATSData';
import type {
  AtsMetaData,
  Section,
} from '@viome/viome-base/src/api/Strapi/StrapiTypes';
import {NavBar} from "@design-system/NavBar.tsx";

const ICON_MAP = {
  shield: SealBarrierIcon,
  droplets: FuelFoundationIcon,
  sprout: ReadyEnvironmentIcon,
} as const;

type IconName = keyof typeof ICON_MAP;

const ICON_NAME_BY_INDEX: IconName[] = ['shield', 'droplets', 'sprout'];

export const TriSynbioticDetailPage = () => {
  const { data, isLoading, isError } = useATSData();

  if (isLoading) {
    return (
      <div className="page-container pb-20">
        <NavBar returnButtonTitle="Back" className="md:-ml-8 mt-8 mb-8" />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-primary" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="page-container pb-20">
        <NavBar returnButtonTitle="Back" className="md:-ml-8 mt-8 mb-8" />
        <div className="flex items-center justify-center py-20">
          <p className="text-sm text-gray-500">
            Unable to load content. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  const sections = getATSSections(data);

  return (
    <div className="page-container pb-20 font-sans">
       <NavBar returnButtonTitle="Back" className="md:-ml-8 mt-8 mb-8" />

      <div className="max-w-2xl">
        <HeroSection data={data} />

        {sections.howToUse && sections.howToUse.items?.length && (
          <>
            <HeroDivider />
            <HowToUseItSection section={sections.howToUse} />
          </>
        )}

        {sections.howItWorks && sections.howItWorks.items?.length && (
          <>
            <Divider />
            <HowItWorksSection section={sections.howItWorks} />
          </>
        )}

        {(sections.whatsInside?.items?.length ||
          sections.supplement?.ingredients?.length ||
          sections.delivered) && (
          <>
            <Divider />
            <WhatsInsideSection
              whatsInside={sections.whatsInside}
              supplement={sections.supplement}
              delivered={sections.delivered}
            />
          </>
        )}

        {sections.faq && sections.faq.faqs?.length && (
          <>
            <Divider />
            <FAQSection section={sections.faq} />
          </>
        )}

        {sections.disclaimer?.text && (
          <DisclaimerSection text={sections.disclaimer.text} />
        )}
      </div>
    </div>
  );
};

const Divider = () => <div className="border-t border-gray-200 my-8" />;

const HeroDivider = () => (
  <div className="border-t border-gray-200 mt-[66px] mb-8" />
);

const SectionLabel = ({ children }: { children: string }) => (
  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-[1.1px] mb-5">
    {children}
  </p>
);

function HeroSection({ data }: { data: AtsMetaData }) {
  return (
    <div className="flex flex-col items-start gap-9">
      {data.titleBadge && (
        <span
          className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold text-[#146464]"
          style={{ border: '0.56px solid #009E82' }}
        >
          {data.titleBadge}
        </span>
      )}
      <img
        src={LOGO_WHITE}
        alt="Viome"
        className="h-5 object-contain invert"
      />
      {data.title && (
        <h1 className="text-3xl font-bold text-primary leading-tight whitespace-pre-line">
          {data.title.replace(/\s(?=TriSynbiotic)/, '\n')}
        </h1>
      )}
    </div>
  );
}

function HowToUseItSection({ section }: { section: Section }) {
  return (
    <div>
      <SectionLabel>{section.title}</SectionLabel>
      <div className="flex flex-col">
        {section.items?.map((step, idx) => (
          <div
            key={idx}
            className={cn(
              'flex gap-3.5 pt-3.5',
              idx > 0 && 'border-t border-gray-100',
            )}
          >
            <div className="bg-[#111] text-white rounded-full w-[26px] h-[26px] flex items-center justify-center shrink-0 text-xs font-medium">
              {idx + 1}
            </div>
            <div className="flex flex-col flex-1 min-w-0 pb-4">
              <p className="text-sm font-medium text-[#111] tracking-[-0.14px]">
                {step.title}
              </p>
              <p className="text-[13px] text-[#555] leading-[20.8px] mt-1">
                {step.description.split('**').map((part, i) =>
                  i % 2 === 1 ? (
                    <span key={i} className="font-medium text-[#111]">
                      {part}
                    </span>
                  ) : (
                    <span key={i}>{part}</span>
                  ),
                )}
              </p>
              {step.notes && (
                <div className="bg-[#ebebeb] rounded-[6px] px-3 py-2.5 mt-2.5">
                  <p className="text-[12px] text-[#666] leading-[18.6px]">
                    {step.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HowItWorksSection({ section }: { section: Section }) {
  return (
    <div>
      <SectionLabel>{section.title}</SectionLabel>
      <div className="flex flex-col">
        {section.items?.map((item, idx) => {
          const iconName =
            (item.badge as IconName) ?? ICON_NAME_BY_INDEX[idx] ?? 'shield';
          const IconComponent = ICON_MAP[iconName] ?? SealBarrierIcon;
          return (
            <div
              key={idx}
              className={cn(
                'flex gap-3.5 pt-3.5',
                idx > 0 && 'border-t border-gray-100',
              )}
            >
              <div className="bg-[#f0f8f7] rounded-full w-8 h-8 flex items-center justify-center shrink-0">
                <IconComponent className="w-4 h-4 text-[#009E82]" />
              </div>
              <div className="flex flex-col gap-1 pb-4">
                <p className="text-sm font-medium text-[#111] tracking-tight">
                  {item.title}
                </p>
                <p className="text-[13px] text-[#555] leading-[1.6]">
                  {item.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WhatsInsideSection({
  whatsInside,
  supplement,
  delivered,
}: {
  whatsInside?: Section;
  supplement?: Section;
  delivered?: Section;
}) {
  return (
    <div>
      <SectionLabel>{"What's inside"}</SectionLabel>

      {whatsInside?.items && whatsInside.items.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-200">
          {whatsInside.items.map((item, idx) => (
            <div key={idx} className="px-4 py-4">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-sm font-medium text-[#111] tracking-tight">
                  {item.title}
                </p>
                {item.badge && (
                  <span className="bg-[#F0F8F7] text-[#146464] text-[11px] font-medium rounded-full px-2.5 py-0.5 shrink-0 ml-2">
                    {item.badge}
                  </span>
                )}
              </div>
              <p className="text-[12.5px] text-[#666] leading-[20px]">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {delivered && (
        <div className="bg-[#111] rounded-md p-5 mt-8">
          {delivered.title && (
            <p className="text-[15px] font-medium text-white tracking-tight">
              {delivered.title}
            </p>
          )}
          {delivered.description && (
            <p className="text-[13px] text-white/60 leading-[1.6] mt-2">
              {delivered.description}
            </p>
          )}
          {delivered.highlights && delivered.highlights.length > 0 && (
            <div className="flex gap-2.5 mt-5">
              {delivered.highlights.map((stat, idx) => (
                <div
                  key={idx}
                  className="flex-1 bg-white/[0.08] rounded p-3"
                >
                  <p className="text-xl font-medium text-[#009e82] tracking-tight">
                    {stat.value}
                  </p>
                  <p className="text-[11px] text-white mt-0.5">
                    {stat.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {supplement && <SupplementFactsPanel section={supplement} />}
    </div>
  );
}

function SupplementFactsPanel({ section }: { section: Section }) {
  return (
    <div className="border-2 border-[#888] mt-8">
      <div className="border-b-[6px] border-[#888] p-3.5">
        <p className="text-[22px] font-bold text-[#888] tracking-tight">
          Supplement Facts
        </p>
        {section.servingSize && (
          <p className="text-xs text-[#888] mt-1">
            {section.servingSize.label}: {section.servingSize.value}
          </p>
        )}
        {section.servingsPerContainer && (
          <p className="text-xs text-[#888]">
            {section.servingsPerContainer.label}:{' '}
            {section.servingsPerContainer.value}
          </p>
        )}
      </div>

      <div className="border-b-[2px] border-[#888] flex justify-end gap-5 px-3.5 py-1.5">
        <span className="text-[10px] font-medium text-[#888]">
          Amount Per Serving
        </span>
        <span className="text-[10px] font-medium text-[#888]">%DV</span>
      </div>

      {section.ingredients?.map((ing, idx) => {
        const hasComponents = !!ing.components?.length;
        return (
          <div key={idx}>
            <div
              className={cn(
                'flex items-baseline justify-between px-3.5 py-1.5',
                hasComponents
                  ? 'border-b-[2px] border-[#888]'
                  : 'border-b border-[#888]',
              )}
            >
              <p
                className={cn(
                  'text-xs text-[#888]',
                  hasComponents && 'font-bold',
                )}
              >
                {ing.name}
              </p>
              <div className="flex gap-5">
                <span className="text-xs text-[#888] text-right">
                  {ing.amount}
                </span>
                <span className="text-xs text-[#888] text-right w-5">
                  {ing.dailyValue}
                </span>
              </div>
            </div>
            {ing.components && ing.components.length > 0 && (
              <div className="px-3.5 py-1">
                {ing.components.map((comp, cIdx) => (
                  <p
                    key={cIdx}
                    className="text-xs text-[#888] italic pl-3.5 leading-relaxed"
                  >
                    {comp}
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {section.footnote && (
        <div className="border-t-[2px] border-[#888] px-3.5 py-2">
          <p className="text-[10px] text-[#888]">{section.footnote}</p>
        </div>
      )}

      {section.otherIngredients && (
        <div className="border-t border-[#888] px-3.5 py-2.5">
          <p className="text-[11px] text-[#888]">
            <span className="font-bold">{section.otherIngredients.label}:</span>{' '}
            {section.otherIngredients.items.join(', ')}
          </p>
        </div>
      )}

      {section.manufacturingInfo && (
        <div className="px-3.5 py-2.5">
          <p className="text-[10.5px] text-[#888] leading-relaxed">
            {section.manufacturingInfo.label}
          </p>
          <p className="text-[10.5px] text-[#888] leading-relaxed">
            {[section.manufacturingInfo.address, section.manufacturingInfo.phone]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      )}

      {section.legal && section.legal.length > 0 && (
        <div className="border-t border-[#888] px-3.5 py-3">
          {section.legal.map((text, idx) => (
            <p
              key={idx}
              className="text-[10px] text-[#888] leading-[1.6] mb-2 last:mb-0"
            >
              {text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function FAQSection({ section }: { section: Section }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div>
      <SectionLabel>{section.title}</SectionLabel>
      <div className="divide-y divide-gray-200">
        {section.faqs?.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div key={idx} className="py-1">
              <button
                className="flex items-center justify-between w-full py-3 text-left"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
              >
                <span className="text-sm font-medium text-[#111] tracking-tight pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  className={cn(
                    'h-[18px] w-[18px] text-gray-500 shrink-0 transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
              {isOpen && (
                <p className="text-[13px] text-[#555] leading-[1.65] pb-3">
                  {faq.answer}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DisclaimerSection({ text }: { text: string }) {
  return (
    <div className="border-2 border-[#888] p-3.5 mt-8">
      <p className="text-[10px] font-semibold text-[#888] leading-[1.6]">
        {text}
      </p>
    </div>
  );
}
